import React, { useState, useEffect } from 'react';
import { X, Instagram, Facebook, MessageCircle, Share2, Clock, Check, Copy } from 'lucide-react';
import { generateOverlayPreview, type OverlayData } from '../services/OverlayGenerator';
import socialShareService, { 
  generateCaption, 
  getBestTimeToPost, 
  shareToInstagram, 
  shareToFacebook, 
  shareToWhatsApp,
  trackShare,
  type ShareData 
} from '../services/socialShareService';

interface ShareToSocialModalProps {
  isOpen: boolean;
  onClose: () => void;
  postId: string;
  userId: string;
  imageUrl: string;
  initialCaption?: string;
  species?: string;
  weight?: number;
  location?: string;
  state?: string;
  checkIn?: {
    tenantId: string;
    name: string;
    instagram?: string;
  };
}

export const ShareToSocialModal: React.FC<ShareToSocialModalProps> = ({
  isOpen,
  onClose,
  postId,
  userId,
  imageUrl,
  initialCaption,
  species,
  weight,
  location,
  state,
  checkIn,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [loading, setLoading] = useState(false);
  const [shared, setShared] = useState<string | null>(null);
  const [copiedCaption, setCopiedCaption] = useState(false);
  
  const bestTime = getBestTimeToPost();

  // Gera preview e caption quando abre
  useEffect(() => {
    if (isOpen && imageUrl) {
      generatePreview();
      generateInitialCaption();
    }
  }, [isOpen, imageUrl]);

  const generatePreview = async () => {
    const overlayData: OverlayData = {
      species,
      weight,
      location,
      state,
      checkIn,
      showBranding: true,
    };

    try {
      const preview = await generateOverlayPreview(imageUrl, overlayData);
      setPreviewUrl(preview);
    } catch (err) {
      console.error('Erro ao gerar preview:', err);
      setPreviewUrl(imageUrl); // Fallback para imagem original
    }
  };

  const generateInitialCaption = () => {
    const shareData: ShareData = {
      postId,
      userId,
      imageUrl,
      caption: initialCaption,
      species,
      weight,
      location,
      state,
      checkIn,
    };
    setCaption(generateCaption(shareData));
  };

  const handleShare = async (platform: 'instagram' | 'facebook' | 'whatsapp') => {
    setLoading(true);
    
    try {
      // Gera blob da imagem com overlay
      const response = await fetch(previewUrl || imageUrl);
      const blob = await response.blob();
      
      // Compartilha
      switch (platform) {
        case 'instagram':
          await shareToInstagram(blob, caption);
          break;
        case 'facebook':
          await shareToFacebook(blob, caption);
          break;
        case 'whatsapp':
          shareToWhatsApp(caption, imageUrl);
          break;
      }
      
      // Registra tracking
      await trackShare(platform, {
        postId,
        userId,
        imageUrl,
        caption,
        species,
        weight,
        location,
        state,
        checkIn,
      });
      
      setShared(platform);
    } catch (err) {
      console.error('Erro ao compartilhar:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopiedCaption(true);
      setTimeout(() => setCopiedCaption(false), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-gray-900 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-bold text-white">Compartilhar nas Redes</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Preview */}
        <div className="p-4">
          <div className="rounded-xl overflow-hidden mb-4 bg-gray-800">
            {previewUrl ? (
              <img 
                src={previewUrl} 
                alt="Preview com overlay" 
                className="w-full"
              />
            ) : (
              <div className="h-64 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent" />
              </div>
            )}
          </div>

          {/* Dica de horário */}
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-4 bg-gray-800/50 px-3 py-2 rounded-lg">
            <Clock className="w-4 h-4 text-amber-400" />
            <span>{bestTime.message}</span>
          </div>

          {/* Caption */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-400">Legenda</label>
              <button
                onClick={handleCopyCaption}
                className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300"
              >
                {copiedCaption ? (
                  <>
                    <Check className="w-3 h-3" />
                    Copiado!
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    Copiar
                  </>
                )}
              </button>
            </div>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white text-sm resize-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              rows={6}
            />
          </div>

          {/* Botões de compartilhamento */}
          <div className="space-y-3">
            <button
              onClick={() => handleShare('instagram')}
              disabled={loading}
              className={`w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl font-semibold transition-all ${
                shared === 'instagram'
                  ? 'bg-green-600 text-white'
                  : 'bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white hover:opacity-90'
              } disabled:opacity-50`}
            >
              {shared === 'instagram' ? (
                <>
                  <Check className="w-5 h-5" />
                  Compartilhado!
                </>
              ) : (
                <>
                  <Instagram className="w-5 h-5" />
                  Compartilhar no Instagram
                </>
              )}
            </button>

            <button
              onClick={() => handleShare('facebook')}
              disabled={loading}
              className={`w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl font-semibold transition-all ${
                shared === 'facebook'
                  ? 'bg-green-600 text-white'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              } disabled:opacity-50`}
            >
              {shared === 'facebook' ? (
                <>
                  <Check className="w-5 h-5" />
                  Compartilhado!
                </>
              ) : (
                <>
                  <Facebook className="w-5 h-5" />
                  Compartilhar no Facebook
                </>
              )}
            </button>

            <button
              onClick={() => handleShare('whatsapp')}
              disabled={loading}
              className={`w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl font-semibold transition-all ${
                shared === 'whatsapp'
                  ? 'bg-green-600 text-white'
                  : 'bg-green-500 text-white hover:bg-green-600'
              } disabled:opacity-50`}
            >
              {shared === 'whatsapp' ? (
                <>
                  <Check className="w-5 h-5" />
                  Compartilhado!
                </>
              ) : (
                <>
                  <MessageCircle className="w-5 h-5" />
                  Enviar pelo WhatsApp
                </>
              )}
            </button>
          </div>

          {/* Nota sobre imagem */}
          <p className="text-xs text-gray-500 text-center mt-4">
            A imagem será copiada para a área de transferência. Cole na rede social.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ShareToSocialModal;
